import {
  Dialog,
  Menu,
  MenuList,
  useMediaQuery,
  useTheme,
  type MenuProps,
} from '@mui/material';
import { isEmbeddableWidgetBundle } from './embeddableWidgetPortalUtils';

/**
 * Drop-in replacement for `Menu` for row and toolbar action menus that also
 * render inside the embeddable widget bundle.
 *
 * A `Menu` is anchored to its trigger and positioned against the browser
 * viewport (`embeddablePopoverPaperSx` forces `position: fixed` so it lines up
 * with the trigger at all). Inside a host page that means the menu detaches from
 * the widget as soon as the host scrolls, and it floats over unrelated host
 * content. Presenting it as a Dialog reuses what the date pickers already do in
 * widgets: `createEmbeddableWidgetTheme` scopes MuiDialog to the host mount
 * node, so the menu is centred in the widget container over a backdrop covering
 * only that container, and it stays put while the host scrolls.
 *
 * Outside the widget bundle, and on real mobile viewports, this is a plain
 * anchored `Menu`.
 */
export function EmbeddableMenu({ children, ...menuProps }: MenuProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!isEmbeddableWidgetBundle || isMobile) {
    return <Menu {...menuProps}>{children}</Menu>;
  }

  const { id, open, onClose, onClick, slotProps } = menuProps;
  // Call sites use the list slot only to point at their trigger's label, so
  // that is the one prop worth carrying over to the dialog's list.
  const listSlotProps =
    typeof slotProps?.list === 'function' ? undefined : slotProps?.list;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={onClick}
      maxWidth={false}
      slotProps={{ paper: { sx: { minWidth: 200, maxWidth: '90%' } } }}
    >
      <MenuList
        id={id}
        aria-labelledby={listSlotProps?.['aria-labelledby']}
      >
        {children}
      </MenuList>
    </Dialog>
  );
}
